import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useMemo, useState } from "react";

import { addDays, format } from "date-fns";

import { useAppSelector } from "app/hooks";
import {
  APPOINTMENT_STATUS_DRAFT,
  QUICK_FILTER_CANCELED_APPOINTMENTS,
  QUICK_FILTER_DEFAULT_TYPE,
  QUICK_FILTER_DRAFT_APPOINTMENTS,
  QUICK_FILTER_INCOMPLETE_TYPE,
  QUICK_FILTER_OPPORTUNITY_TYPE,
  QUICK_FILTER_UNCONFIRMED_APPOINTMENTS_TYPE,
  VISIBLE_STATUSES_CODES
} from "constants/appointments";
import {
  APPOINTMENT_COUNTERS_FEATURE,
  WEB_SCHEDULER_APPOINTMENTS_FILTER
} from "constants/feature-flags";
import { useLazyQueryAppointmentsCounterQuery } from "services/appointments";
import { QuerySearchFilter } from "services/types/appointments.type";

import { selectFilterValues, selectFiltersReady } from "../scheduler-slice";

export default function useAppointmentCounters() {
  const [queriesCounters, setQueriesCounters] = useState<
    Record<string, number>
  >({
    [QUICK_FILTER_DEFAULT_TYPE]: 0,
    [QUICK_FILTER_UNCONFIRMED_APPOINTMENTS_TYPE]: 0,
    [QUICK_FILTER_OPPORTUNITY_TYPE]: 0,
    [QUICK_FILTER_INCOMPLETE_TYPE]: 0,
    [QUICK_FILTER_DRAFT_APPOINTMENTS]: 0,
    [QUICK_FILTER_CANCELED_APPOINTMENTS]: 0
  });
  const flags = useFlags();
  const appointmentCountersEnabled = flags[APPOINTMENT_COUNTERS_FEATURE];
  const appointmentDraftsEnabled = flags[WEB_SCHEDULER_APPOINTMENTS_FILTER];

  const [getAppointmentsCounter, { isUninitialized, isLoading, isFetching }] =
    useLazyQueryAppointmentsCounterQuery();

  const filtersReady = useAppSelector(selectFiltersReady);
  const appointmentsFilters = useAppSelector(selectFilterValues);
  // SN-19768: the first request only. `isLoading` stays false once the hook holds any
  // counters, so the refetch that follows every tag or status write does not read as a
  // first load. Only this flag may gate the events query.
  const isInitialLoadingCounters = useMemo(
    () => appointmentCountersEnabled && Boolean(isUninitialized || isLoading),
    [isUninitialized, isLoading, appointmentCountersEnabled]
  );
  const isLoadingCounters = useMemo(
    () =>
      appointmentCountersEnabled &&
      Boolean(isUninitialized || isLoading || isFetching),
    [isUninitialized, isLoading, isFetching, appointmentCountersEnabled]
  );
  const countersBaseFilter: QuerySearchFilter = useMemo(
    () => ({
      after_moment: appointmentsFilters.fromDate,
      before_moment: format(
        addDays(new Date(appointmentsFilters.toDate), 2),
        "yyyy-MM-dd"
      ),
      clinic: appointmentsFilters.clinicIds,
      staff_member: appointmentsFilters.staffIds,
      status: appointmentDraftsEnabled
        ? VISIBLE_STATUSES_CODES.filter(
            (code) => code !== APPOINTMENT_STATUS_DRAFT
          )
        : VISIBLE_STATUSES_CODES
    }),
    [
      appointmentsFilters.fromDate,
      appointmentsFilters.toDate,
      appointmentsFilters.clinicIds,
      appointmentsFilters.staffIds,
      appointmentDraftsEnabled
    ]
  );

  const refetchAppointmentCounters = useCallback(async () => {
    if (filtersReady) {
      try {
        const { data, error } =
          await getAppointmentsCounter(countersBaseFilter);
        // The lazy trigger resolves with `error` rather than throwing, so the catch
        // below never sees a failed request.
        if (error) {
          console.error("Error fetching appointment counters:", error);
        }
        if (data) {
          const {
            appointmentAll,
            appointmentAllNoDrafts,
            appointmentUnconfirmed,
            appointmentOpportunity,
            appointmentIncomplete,
            appointmentDraft,
            appointmentCancelledRescheduled
          } = data;
          setQueriesCounters({
            [QUICK_FILTER_DEFAULT_TYPE]: appointmentDraftsEnabled
              ? appointmentAllNoDrafts
              : appointmentAll,
            [QUICK_FILTER_UNCONFIRMED_APPOINTMENTS_TYPE]:
              appointmentUnconfirmed,
            [QUICK_FILTER_OPPORTUNITY_TYPE]: appointmentOpportunity,
            [QUICK_FILTER_INCOMPLETE_TYPE]: appointmentIncomplete,
            [QUICK_FILTER_DRAFT_APPOINTMENTS]: appointmentDraft,
            [QUICK_FILTER_CANCELED_APPOINTMENTS]:
              appointmentCancelledRescheduled
          });
        }
      } catch (error) {
        console.error("Error fetching appointment counters:", error);
      }
    }
  }, [
    setQueriesCounters,
    getAppointmentsCounter,
    countersBaseFilter,
    filtersReady,
    appointmentDraftsEnabled
  ]);
  useEffect(() => {
    if (appointmentCountersEnabled && filtersReady) {
      refetchAppointmentCounters();
    }
  }, [filtersReady, refetchAppointmentCounters, appointmentCountersEnabled]);
  return {
    queriesCounters,
    isLoadingCounters,
    isInitialLoadingCounters,
    refetchAppointmentCounters
  };
}
